/**
 * TaskTemplatesPage — Workflow · Task Templates.
 *
 * Reusable task definitions: what kind of task, who it's for, when it
 * auto-creates (trigger event), and how long until it's overdue (SLA). The
 * pipeline + event dispatchers instantiate a task from a template when its
 * trigger fires.
 *
 * Contract (verified 2026-06-16 — AdminWorkflowsService DTO/service field
 * names all match the editor; every field persists; no drift):
 *   - Read:  GET  /v3/config/task-templates → TaskTemplateRow[]
 *   - Write: POST /v3/admin/task-templates (natural key TemplateCode)
 *   - Soft-delete: re-upsert IsActive=false (hard DELETE route exists but the
 *            UI keeps historical rows for audit).
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in the bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  buildAdminAuthHeaders,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, NumberField, TextArea, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface Row {
  templateCode: string;
  displayName: string;
  description: string | null;
  category: string;
  defaultPriority: string;
  triggerEvent: string;
  defaultDueDays: number | null;
  assignmentRule: string;
  isAutoCreated: boolean;
  slaTargetHours: number | null;
  applicabilityPredicateJson: string | null;
  sortOrder: number | null;
  isActive: boolean;
}

interface EditDraft extends Row {
  isNew: boolean;
}

const CATEGORIES: ReadonlyArray<{ value: string; label: string; tone: 'info' | 'ok' | 'warn' | 'muted' }> = [
  { value: 'Card', label: 'Card', tone: 'info' },
  { value: 'Compliance', label: 'Compliance', tone: 'warn' },
  { value: 'Documents', label: 'Documents', tone: 'info' },
  { value: 'Employee', label: 'Employee', tone: 'ok' },
  { value: 'SystemOps', label: 'System Ops', tone: 'muted' },
  { value: 'CrossSell', label: 'Cross-Sell', tone: 'ok' },
  { value: 'Other', label: 'Other', tone: 'muted' },
];

const PRIORITIES = ['Low', 'Normal', 'High', 'Critical'] as const;

const ASSIGNMENT_RULES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'branch-officer', label: "Branch officer (the cart's branch)" },
  { value: 'opening-officer', label: 'Opening officer (the FSR who opened the cart)' },
  { value: 'unassigned', label: 'Unassigned (manual pickup from RTM)' },
];

const TRIGGER_EVENTS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'pipeline.complete', label: 'Pipeline complete' },
  { value: 'pipeline.step.complete', label: 'Specific step complete' },
  { value: 'compliance.gate.failed', label: 'Compliance gate failed' },
  { value: 'cart.abandoned', label: 'Cart abandoned' },
  { value: 'signing.session.complete', label: 'Signing session complete' },
  { value: 'custom', label: 'Custom (manual creation only)' },
];

function categoryTone(c: string): 'info' | 'ok' | 'warn' | 'muted' {
  return CATEGORIES.find((x) => x.value === c)?.tone ?? 'muted';
}

function emptyDraft(): EditDraft {
  return {
    templateCode: '',
    displayName: '',
    description: '',
    category: 'Other',
    defaultPriority: 'Normal',
    triggerEvent: 'custom',
    defaultDueDays: 7,
    assignmentRule: 'branch-officer',
    isAutoCreated: true,
    slaTargetHours: 48,
    applicabilityPredicateJson: '',
    sortOrder: 100,
    isActive: true,
    isNew: true,
  };
}

export function TaskTemplatesPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<Row> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Row | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const resp = await client.http.get<{ success: boolean; data?: ReadonlyArray<Row>; error?: string }>(
          '/v3/config/task-templates',
        );
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) setRows(resp.data);
        else setLoadError(resp.error ?? 'Failed to load task templates.');
      } catch (e) {
        if (cancelled) return;
        setLoadError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const categoryOptions = useMemo(
    () => [{ value: '', label: 'All categories' }, ...CATEGORIES.map((c) => ({ value: c.value, label: c.label }))],
    [],
  );

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (rows ?? [])
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .filter((r) => {
        if (categoryFilter && r.category !== categoryFilter) return false;
        if (needle) {
          const hay = `${r.templateCode} ${r.displayName} ${r.description ?? ''}`.toLowerCase();
          if (!hay.includes(needle)) return false;
        }
        return true;
      });
  }, [rows, categoryFilter, search]);

  const upsert = useCallback(
    async (row: Row): Promise<boolean> => {
      const resp = await client.http.post<{ success: boolean; error?: string }>('/v3/admin/task-templates', {
        headers: buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL),
        body: {
          templateCode: row.templateCode.trim(),
          displayName: row.displayName.trim(),
          description: row.description?.trim() ? row.description.trim() : null,
          category: row.category,
          defaultPriority: row.defaultPriority,
          triggerEvent: row.triggerEvent.trim(),
          defaultDueDays: row.defaultDueDays,
          assignmentRule: row.assignmentRule,
          isAutoCreated: row.isAutoCreated,
          slaTargetHours: row.slaTargetHours,
          applicabilityPredicateJson: row.applicabilityPredicateJson?.trim() ? row.applicabilityPredicateJson.trim() : null,
          sortOrder: row.sortOrder,
          isActive: row.isActive,
        },
      });
      if (!resp.success) throw new Error(resp.error ?? 'admin upsert returned success=false');
      return true;
    },
    [client],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.templateCode.trim() || !draft.displayName.trim()) {
      setSaveError('Template code and friendly name are required.');
      return;
    }
    if (draft.applicabilityPredicateJson?.trim()) {
      try {
        JSON.parse(draft.applicabilityPredicateJson);
      } catch {
        setSaveError('Applicability predicate must be valid JSON (or blank).');
        return;
      }
    }
    setSaving(true);
    setSaveError(null);
    try {
      await upsert(draft);
      setDraft(null);
      setToast({ tone: 'info', text: `Saved. ${draft.displayName} is updated.` });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, upsert, refresh]);

  const handleSoftDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      await upsert({ ...deleteTarget, isActive: false });
      setToast({ tone: 'warn', text: `${deleteTarget.displayName} is now inactive.` });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, upsert, refresh]);

  const columns: ReadonlyArray<DataTableColumn<Row>> = [
    { header: 'Code', cell: (r) => <code className="cell-mono--bold">{r.templateCode}</code> },
    {
      header: 'Friendly name',
      cell: (r) => (
        <span>
          <span className="cell-strong">{r.displayName}</span>
          {r.description && <div className="cell-desc">{r.description}</div>}
        </span>
      ),
    },
    { header: 'Category', cell: (r) => <Pill tone={categoryTone(r.category)}>{r.category}</Pill> },
    { header: 'Trigger', cell: (r) => <code className="cell-mono">{r.triggerEvent}</code> },
    { header: 'Priority', cell: (r) => <span className="cell-desc">{r.defaultPriority}</span> },
    { header: 'Auto', cell: (r) => (r.isAutoCreated ? <Pill tone="info">auto</Pill> : <span className="cell-desc">manual</span>) },
    { header: 'Active', cell: (r) => (r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>) },
    {
      header: '',
      cell: (r) => (
        <div className="card-actions-row">
          <Button onClick={() => { setDraft({ ...r, description: r.description ?? '', applicabilityPredicateJson: r.applicabilityPredicateJson ?? '', isNew: false }); setSaveError(null); }}>
            Edit
          </Button>
          {r.isActive && <Button onClick={() => setDeleteTarget(r)}>Deactivate</Button>}
        </div>
      ),
    },
  ];

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load task templates: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading task templates…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={`Task Templates — ${visible.length} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <SelectField label="" value={categoryFilter} onChange={setCategoryFilter} options={categoryOptions} />
            </div>
            <div className="users-filter">
              <TextField label="" value={search} onChange={setSearch} placeholder="code, name, description…" />
            </div>
            <Button variant="primary" onClick={() => { setDraft(emptyDraft()); setSaveError(null); }}>
              + Add Template
            </Button>
          </div>
        }
      >
        <DataTable<Row>
          columns={columns}
          rows={visible}
          rowKey={(r) => r.templateCode}
          emptyState={<p className="empty">No templates match the current filters.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add a task template' : `Edit ${draft?.displayName ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={720}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <TextField
                label="Template code"
                value={draft.templateCode}
                onChange={(v) => draft.isNew && setDraft({ ...draft, templateCode: v.slice(0, 64) })}
                disabled={!draft.isNew}
                monospace
                placeholder="e.g. card.order.followup"
                hint={draft.isNew ? 'Permanent.' : 'Permanent.'}
              />
              <TextField
                label="Friendly name"
                value={draft.displayName}
                onChange={(v) => setDraft({ ...draft, displayName: v })}
                placeholder="e.g. Order debit card — follow up"
              />
            </div>

            <TextField
              label="Description"
              value={draft.description ?? ''}
              onChange={(v) => setDraft({ ...draft, description: v })}
              placeholder="What this task is for."
            />

            <div className="form-row">
              <SelectField
                label="Category"
                value={draft.category}
                onChange={(v) => setDraft({ ...draft, category: v })}
                options={CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
              />
              <SelectField
                label="Default priority"
                value={draft.defaultPriority}
                onChange={(v) => setDraft({ ...draft, defaultPriority: v })}
                options={PRIORITIES.map((p) => ({ value: p, label: p }))}
              />
            </div>

            <div className="form-row">
              <SelectField
                label="Trigger event"
                value={draft.triggerEvent}
                onChange={(v) => setDraft({ ...draft, triggerEvent: v })}
                options={TRIGGER_EVENTS.map((t) => ({ value: t.value, label: t.label }))}
                hint="When this task auto-creates."
              />
              <SelectField
                label="Assignment"
                value={draft.assignmentRule}
                onChange={(v) => setDraft({ ...draft, assignmentRule: v })}
                options={ASSIGNMENT_RULES.map((a) => ({ value: a.value, label: a.label }))}
              />
            </div>

            <div className="form-row">
              <NumberField
                label="Default due (days)"
                value={draft.defaultDueDays}
                onChange={(v) => setDraft({ ...draft, defaultDueDays: v })}
                min={0}
              />
              <NumberField
                label="SLA target (hours)"
                value={draft.slaTargetHours}
                onChange={(v) => setDraft({ ...draft, slaTargetHours: v })}
                min={0}
              />
            </div>

            <div className="form-row">
              <NumberField
                label="Sort order"
                value={draft.sortOrder}
                onChange={(v) => setDraft({ ...draft, sortOrder: v })}
                min={0}
              />
              <BoolField
                label="Auto-created"
                value={draft.isAutoCreated}
                onChange={(v) => setDraft({ ...draft, isAutoCreated: v })}
                hint="Creates automatically when the trigger fires."
              />
            </div>

            <TextArea
              label="Applicability predicate (JSON, optional)"
              value={draft.applicabilityPredicateJson ?? ''}
              onChange={(v) => setDraft({ ...draft, applicabilityPredicateJson: v })}
              rows={2}
              placeholder='{"product.category":"IRA"}'
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Inactive templates never instantiate."
            />
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={deleteTarget !== null}
        title="Mark this template inactive?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="primary" onClick={() => void handleSoftDelete()} disabled={saving}>
                {saving ? 'Working…' : 'Mark inactive'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We'll set <strong>{deleteTarget.displayName}</strong> (
              <code className="cell-mono">{deleteTarget.templateCode}</code>) to inactive. It stops
              instantiating, but the row stays for audit. Flip back on anytime.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}
